"use client";

import { useId, useMemo } from "react";
import Particles, { ParticlesProvider } from "@tsparticles/react";
import type { Container, Engine, ISourceOptions } from "@tsparticles/engine";
import { loadSlim } from "@tsparticles/slim";
import { cn } from "@/lib/utils";
import { motion, useAnimation } from "motion/react";

const initParticles = async (engine: Engine) => {
  await loadSlim(engine);
};

type ParticlesProps = {
  id?: string;
  className?: string;
  background?: string;
  particleSize?: number;
  minSize?: number;
  maxSize?: number;
  speed?: number;
  particleColor?: string;
  particleDensity?: number;
};

const Sparkles = ({
  id,
  className,
  background = "transparent",
  particleSize,
  minSize,
  maxSize,
  speed = 2,
  particleColor = "#ffffff",
  particleDensity = 80,
}: ParticlesProps) => {
  const controls = useAnimation();
  const generatedId = useId();

  const particlesLoaded = async (container?: Container) => {
    if (container) {
      await controls.start({
        opacity: 1,
        transition: { duration: 1 },
      });
    }
  };

  const options = useMemo<ISourceOptions>(
    () => ({
      background: {
        color: { value: background },
      },
      detectRetina: true,
      fullScreen: {
        enable: false,
      },
      fpsLimit: 60,
      interactivity: {
        events: {
          onClick: {
            enable: false,
          },
          onHover: {
            enable: false,
          },
        },
      },
      particles: {
        color: {
          value: particleColor,
        },
        links: {
          enable: false,
        },
        move: {
          direction: "none",
          enable: true,
          outModes: {
            default: "out",
          },
          random: true,
          speed: {
            min: 0.1,
            max: speed,
          },
          straight: false,
        },
        number: {
          density: {
            enable: true,
            width: 400,
            height: 400,
          },
          value: particleDensity,
        },
        opacity: {
          value: {
            min: 0.15,
            max: 0.9,
          },
          animation: {
            enable: true,
            speed,
            sync: false,
          },
        },
        shape: {
          type: "circle",
        },
        size: {
          value: {
            min: minSize ?? particleSize ?? 0.6,
            max: maxSize ?? particleSize ?? 1.8,
          },
        },
      },
    }),
    [background, maxSize, minSize, particleColor, particleDensity, particleSize, speed],
  );

  return (
    <motion.div animate={controls} className={cn("opacity-0", className)}>
      <Particles
        id={id || generatedId}
        className="h-full w-full"
        particlesLoaded={particlesLoaded}
        options={options}
      />
    </motion.div>
  );
};

export const SparklesCore = (props: ParticlesProps) => (
  <ParticlesProvider init={initParticles}>
    <Sparkles {...props} />
  </ParticlesProvider>
);
